import { useTranslation } from 'react-i18next';
import LandingLayout from '../components/LandingLayout';

export default function TermsPage() {
  const { t } = useTranslation();

  return (
    <LandingLayout>
      <div className="min-h-screen bg-dark-950 py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl md:text-5xl font-extrabold mb-4 text-white">
            {t('legal.terms.title')}
          </h1>
          <p className="text-gray-400 mb-12">
            {t('legal.terms.lastUpdated')}: {t('legal.terms.lastUpdatedDate')}
          </p>

          <div className="space-y-12 text-gray-300">
            {/* Introduction */}
            <section>
              <h2 className="text-2xl font-bold text-white mb-4">
                {t('legal.terms.sections.intro.title')}
              </h2>
              <p className="leading-relaxed">
                {t('legal.terms.sections.intro.content')}
              </p>
            </section>

            {/* Broadcasting License - KEY SECTION */}
            <section className="bg-primary-500/10 border border-primary-500/30 rounded-2xl p-6 md:p-8">
              <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                <span className="text-3xl">📜</span>
                {t('legal.terms.sections.broadcasting.title')}
              </h2>
              <div className="space-y-4">
                <p className="leading-relaxed">
                  {t('legal.terms.sections.broadcasting.content')}
                </p>
                <div className="bg-white/5 rounded-xl p-4 space-y-3">
                  <p className="flex items-start gap-3">
                    <span className="text-green-400 text-xl">✓</span>
                    <span>{t('legal.terms.sections.broadcasting.activeOnly')}</span>
                  </p>
                  <p className="flex items-start gap-3">
                    <span className="text-green-400 text-xl">✓</span>
                    <span>{t('legal.terms.sections.broadcasting.validPeriod')}</span>
                  </p>
                  <p className="flex items-start gap-3">
                    <span className="text-red-400 text-xl">✗</span>
                    <span>{t('legal.terms.sections.broadcasting.termination')}</span>
                  </p>
                </div>
              </div>
            </section>

            {/* License Certificate */}
            <section>
              <h2 className="text-2xl font-bold text-white mb-4">
                {t('legal.terms.sections.certificate.title')}
              </h2>
              <p className="leading-relaxed">
                {t('legal.terms.sections.certificate.content')}
              </p>
            </section>

            {/* Subscription */}
            <section>
              <h2 className="text-2xl font-bold text-white mb-4">
                {t('legal.terms.sections.subscription.title')}
              </h2>
              <div className="space-y-4">
                <p className="leading-relaxed">
                  {t('legal.terms.sections.subscription.content')}
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>{t('legal.terms.sections.subscription.item1')}</li>
                  <li>{t('legal.terms.sections.subscription.item2')}</li>
                  <li>{t('legal.terms.sections.subscription.item3')}</li>
                </ul>
              </div>
            </section>

            {/* Permitted Use */}
            <section>
              <h2 className="text-2xl font-bold text-white mb-4">
                {t('legal.terms.sections.permitted.title')}
              </h2>
              <div className="space-y-4">
                <p className="leading-relaxed">
                  {t('legal.terms.sections.permitted.content')}
                </p>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4">
                    <h3 className="font-semibold text-green-400 mb-2">{t('legal.terms.sections.permitted.allowed')}</h3>
                    <ul className="space-y-2 text-sm">
                      <li className="flex items-center gap-2">
                        <span className="text-green-400">✓</span>
                        {t('legal.terms.sections.permitted.allowed1')}
                      </li>
                      <li className="flex items-center gap-2">
                        <span className="text-green-400">✓</span>
                        {t('legal.terms.sections.permitted.allowed2')}
                      </li>
                      <li className="flex items-center gap-2">
                        <span className="text-green-400">✓</span>
                        {t('legal.terms.sections.permitted.allowed3')}
                      </li>
                    </ul>
                  </div>
                  <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4">
                    <h3 className="font-semibold text-red-400 mb-2">{t('legal.terms.sections.permitted.prohibited')}</h3>
                    <ul className="space-y-2 text-sm">
                      <li className="flex items-center gap-2">
                        <span className="text-red-400">✗</span>
                        {t('legal.terms.sections.permitted.prohibited1')}
                      </li>
                      <li className="flex items-center gap-2">
                        <span className="text-red-400">✗</span>
                        {t('legal.terms.sections.permitted.prohibited2')}
                      </li>
                      <li className="flex items-center gap-2">
                        <span className="text-red-400">✗</span>
                        {t('legal.terms.sections.permitted.prohibited3')}
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </section>

            {/* Intellectual Property */}
            <section>
              <h2 className="text-2xl font-bold text-white mb-4">
                {t('legal.terms.sections.ip.title')}
              </h2>
              <p className="leading-relaxed">
                {t('legal.terms.sections.ip.content')}
              </p>
            </section>

            {/* Termination */}
            <section>
              <h2 className="text-2xl font-bold text-white mb-4">
                {t('legal.terms.sections.termination.title')}
              </h2>
              <p className="leading-relaxed">
                {t('legal.terms.sections.termination.content')}
              </p>
            </section>

            {/* Limitation of Liability */}
            <section>
              <h2 className="text-2xl font-bold text-white mb-4">
                {t('legal.terms.sections.liability.title')}
              </h2>
              <p className="leading-relaxed">
                {t('legal.terms.sections.liability.content')}
              </p>
            </section>

            {/* Governing Law */}
            <section>
              <h2 className="text-2xl font-bold text-white mb-4">
                {t('legal.terms.sections.law.title')}
              </h2>
              <p className="leading-relaxed">
                {t('legal.terms.sections.law.content')}
              </p>
            </section>

            {/* Contact */}
            <section>
              <h2 className="text-2xl font-bold text-white mb-4">
                {t('legal.terms.sections.contact.title')}
              </h2>
              <p className="leading-relaxed">
                {t('legal.terms.sections.contact.content')}
              </p>
              <p className="mt-4 text-primary-400">
                {t('legal.contact.email')}
              </p>
            </section>
          </div>
        </div>
      </div>
    </LandingLayout>
  );
}
